/**
 * The activity register's contract — `GET /api/v1/foundations/activity` (bata-platform#482,
 * bata-platform ADR 2009), as the pinned client generates it. `docs/adr/9037`.
 *
 * ══ PINNED ═══════════════════════════════════════════════════════════════════
 *
 * `src/api/spec/openapi.json` is bata-platform v0.57.0, which publishes the read (#482, with
 * #484's additive fields and alert words). So the types below are the generated ones, re-exported
 * under the names the screen, the export and the tests already import, and `ACTIVITY_READ` is the
 * generated operation. Until this re-pin the file held #482's shapes typed by hand and
 * `ACTIVITY_READ = null`; the generated shapes matched them field for field, and the action
 * vocabulary word for word (17 words, `accepted`, `raised` and `unassigned` included).
 *
 * `activity.test.tsx` keeps the tripwire: the pinned spec has the path exactly when
 * `ACTIVITY_READ` is wired. A platform that does not serve it yet (an API older than v0.57.0)
 * answers 404, which the page draws as «In arrivo» (ADR 9037).
 */

import {
  readActivity,
  type ActivityAction, type ActivityApp, type ActivityEntry, type ActivityPage, type ActivitySource,
  type ReadActivityParams,
} from '@/api/generated';

export type { ActivityAction, ActivityApp, ActivityEntry, ActivityPage, ActivitySource };

/** The five apps an activity is recorded under — the generated `ActivityApp`, in its order. */
export const ACTIVITY_APPS = ['pricing', 'replenishment', 'compacting', 'management', 'foundations'] as const satisfies readonly ActivityApp[];

/**
 * What was done — the generated `ActivityAction`, fixed and in the past tense, in its order. The
 * last three are bata-platform#484's for its alert sources: `accepted` (an alert accepted — not an
 * approval), `raised` (the exception sweep raising an alert), `unassigned` (an alert released).
 * Each has words in the three catalogues. `activity.test.tsx` holds this list equal to the pinned
 * spec's enum, so a word the platform adds fails there until it has words here.
 */
export const ACTIVITY_ACTIONS = [
  'proposed', 'approved', 'rejected', 'adjusted', 'deferred', 'exported', 'confirmed', 'assigned', 'snoozed',
  'reopened', 'simulated', 'adopted', 'reversed', 'withdrawn', 'accepted', 'raised', 'unassigned',
] as const satisfies readonly ActivityAction[];

/**
 * The read's query as this page sends it: the generated query, with `from` and `to` required —
 * the platform requires both (inclusive; the platform bounds the window), and the generator types
 * every query member optional.
 */
export type ActivityQuery = NonNullable<ReadActivityParams['query']> & { readonly from: string; readonly to: string };

/** The largest page #482 serves (`limit` max 500) — what the CSV's walk asks for. */
export const ACTIVITY_MAX_LIMIT = 500;

export type ListActivityParams = ReadActivityParams;

export type ActivityRead = typeof readActivity;

/** The generated operation. */
export const ACTIVITY_READ: ActivityRead = readActivity;

/** The path the platform serves it at — what the re-pin tripwire looks for in the spec. */
export const ACTIVITY_PATH_IN_SPEC = '/api/v1/foundations/activity';
